import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu } from '@headlessui/react';
import {
  Bars3Icon,
  BellIcon,
  SunIcon,
  MoonIcon,
  ChatBubbleBottomCenterTextIcon
} from '@heroicons/react/24/outline';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import { useRealtimeData } from '../../contexts/RealtimeDataContext';
import BusinessSwitcher from './BusinessSwitcher';
import { db, auth } from '../../services/firebase';
import { collection, query, where, onSnapshot, orderBy, limit, getDocs } from 'firebase/firestore';

import { FirebaseBusinessService } from '../../services/firebase/business';
import { isAdminUser } from '../../utils/userUtils';

// Logo ve Favicon imports
const logoLight = process.env.PUBLIC_URL + '/icons/sp-logo-blue.svg';
const logoDark = process.env.PUBLIC_URL + '/icons/sp-logo-white.svg';
const iconFav = process.env.PUBLIC_URL + '/icons/sp-fav-blue.svg';

const Header = ({ onMenuToggle, darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const {
    currentUser,
    isAuthenticated,
    selectedBusiness,
    logout
  } = useAuthBusiness();

  const [userDisplayData, setUserDisplayData] = useState(currentUser);
  const [notifications, setNotifications] = useState([]);
  const [readIds, setReadIds] = useState([]);
  const [businessLogo, setBusinessLogo] = useState(null);

  useEffect(() => {
    setUserDisplayData(currentUser);
  }, [currentUser]);

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState({ menu: [], orders: [], pages: [] });
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 2) {
      setSearchResults({ menu: [], orders: [], pages: [] });
      setShowDropdown(false);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      setIsSearching(true);
      setShowDropdown(true);

      const businessId = localStorage.getItem('currentBusinessId') || selectedBusiness?.id;
      
      try {
        const queryTerm = searchTerm.toLowerCase();
        const isAdmin = isAdminUser(currentUser);

        // 1. Fetch from 'menu' and 'orders' (only if a business is selected)
        let matchingMenu = [];
        let matchingOrders = [];
        
        if (businessId && businessId !== 'business_account') {
          try {
            const menuRef = collection(db, 'businesses', 'food_beverage', 'businesses', businessId, 'menu');
            const menuSnap = await getDocs(menuRef);
            matchingMenu = menuSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(item => 
              item.name?.toLowerCase().includes(queryTerm) ||
              item.description?.toLowerCase().includes(queryTerm)
            ).slice(0, 5);

            const ordersRef = collection(db, 'businesses', 'food_beverage', 'businesses', businessId, 'orders');
            const ordersSnap = await getDocs(ordersRef);
            matchingOrders = ordersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(order => 
              order.orderNumber?.toString().includes(queryTerm) ||
              order.customerName?.toLowerCase().includes(queryTerm) ||
              order.customer?.name?.toLowerCase().includes(queryTerm) ||
              order.table_number?.toString().includes(queryTerm)
            ).slice(0, 5);
          } catch (e) {
             // Collection might not exist for some sectors yet
          }
        }

        // 2. Admin Specific Search: Business Accounts
        let matchingAdminUsers = [];
        if (isAdmin) {
          const bizAccountsRef = collection(db, 'business_accounts');
          const bizAccountsSnap = await getDocs(bizAccountsRef);
          matchingAdminUsers = bizAccountsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(acc => 
            (acc.name || acc.full_name || acc.displayName || '').toLowerCase().includes(queryTerm) ||
            (acc.email || '').toLowerCase().includes(queryTerm)
          ).slice(0, 5).map(acc => ({
            name: acc.name || acc.full_name || acc.displayName || acc.email,
            path: `/dashboard/admin/users`,
            type: 'customer',
            subtitle: acc.email
          }));
        }

        // 3. Search Pages (Dynamic based on Role)
        const pages = [
          // Admin Pages
          { name: 'Müşteri Yönetimi', path: '/dashboard/admin/users', roles: ['admin', 'super_admin'] },
          { name: 'Sistem Personeli', path: '/dashboard/admin/system-users', roles: ['admin', 'super_admin'] },
          { name: 'Sektör Ayarları', path: '/dashboard/admin/sectors', roles: ['admin', 'super_admin'] },
          { name: 'Sistem Ayarları', path: '/dashboard/admin/settings', roles: ['admin', 'super_admin'] },
          { name: 'Fatura & Ödeme', path: '/dashboard/admin/billing', roles: ['admin', 'super_admin'] },
          { name: 'Güvenlik & 2FA', path: '/dashboard/admin/security', roles: ['admin', 'super_admin'] },
          { name: 'Modül Yönetimi', path: '/dashboard/admin/modules', roles: ['admin', 'super_admin'] },
          { name: 'WhatsApp Panel', path: '/dashboard/admin/whatsapp', roles: ['admin', 'super_admin'] },
          { name: 'Yedekleme & Geri Yükleme', path: '/dashboard/admin/backup', roles: ['admin', 'super_admin'] },
          { name: 'Log Kayıtları', path: '/dashboard/admin/logs', roles: ['admin', 'super_admin'] },
          
          // Business Pages
          { name: 'Menü Yönetimi', path: '/sectors/food-beverage/settings/menu', roles: ['business_owner', 'manager'] },
          { name: 'Sipariş Takibi', path: '/sectors/food-beverage/operations/order-center', roles: ['business_owner', 'manager', 'staff'] },
          { name: 'Hızlı Satış POS', path: '/sectors/food-beverage/pos/quick-sales', roles: ['business_owner', 'manager', 'staff'] },
          { name: 'Masa Yönetimi', path: '/sectors/food-beverage/pos/table-service', roles: ['business_owner', 'manager', 'staff'] },
          { name: 'Raporlar ve Analitik', path: '/sectors/food-beverage/reports/dashboard', roles: ['business_owner', 'manager'] },
          { name: 'İşletme Ayarları', path: '/sectors/food-beverage/settings', roles: ['business_owner', 'manager'] }
        ];

        const matchingPages = pages.filter(p => 
          p.name.toLowerCase().includes(queryTerm) && 
          (!p.roles || p.roles.includes(currentUser?.role || 'staff') || isAdmin)
        );

        setSearchResults({
          menu: matchingMenu,
          orders: matchingOrders,
          pages: [...matchingPages, ...matchingAdminUsers]
        });
      } catch (err) {
        console.error('Search error:', err);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentUser, selectedBusiness]);


  // Load business logo if missing
  useEffect(() => {
    if (selectedBusiness?.id && selectedBusiness.id !== 'business_account') {
      const currentLogo = selectedBusiness.logo || selectedBusiness.logo_url || selectedBusiness.logoUrl;
      if (currentLogo) {
        setBusinessLogo(currentLogo);
      } else {
        FirebaseBusinessService.getBusinessById(selectedBusiness.id).then(biz => {
          const fetchedLogo = biz?.logo || biz?.logo_url || biz?.logoUrl;
          if (fetchedLogo) {
            setBusinessLogo(fetchedLogo);
          }
        }).catch(err => console.error("Logo fetch error:", err));
      }
    } else {
      setBusinessLogo(null);
    }
  }, [selectedBusiness]);

  // Load read status from local storage
  useEffect(() => {
    const storedRead = localStorage.getItem('readNotifications');
    if (storedRead) {
      setReadIds(JSON.parse(storedRead));
    }
  }, []);

  const markAsRead = (id) => {
    if (!readIds.includes(id)) {
      const newReadIds = [...readIds, id];
      setReadIds(newReadIds);
      localStorage.setItem('readNotifications', JSON.stringify(newReadIds));
    }
  };

  const unreadCount = notifications.filter(n => !readIds.includes(n.id)).length;

  // Listen for profile updates
  useEffect(() => {
    const handleProfileUpdate = (event) => {
      const updatedUser = event.detail;
      setUserDisplayData(updatedUser);
    };
    window.addEventListener('userProfileUpdated', handleProfileUpdate);
    return () => {
      window.removeEventListener('userProfileUpdated', handleProfileUpdate);
    };
  }, []);

  // Update userDisplayData when currentUser changes
  useEffect(() => {
    setUserDisplayData(currentUser);
  }, [currentUser]);

  // Notifications - useRealtimeData context üzerinden merkezi okunuyor (0 ek Firestore dinleyicisi)
  const { notifications: realtimeNotifications } = useRealtimeData();

  useEffect(() => {
    if (Array.isArray(realtimeNotifications)) {
      const sorted = [...realtimeNotifications].sort((a, b) => {
        const getMillis = (d) => {
          if (!d) return 0;
          if (d.toDate) return d.toDate().getTime();
          if (d instanceof Date) return d.getTime();
          return new Date(d).getTime() || 0;
        };
        return getMillis(b.createdAt) - getMillis(a.createdAt);
      });
      setNotifications(sorted.slice(0, 10));
    }
  }, [realtimeNotifications]);

  return (
    <nav 
      className={`relative flex flex-nowrap items-center justify-between px-4 py-2.5 w-full transition-all shadow-sm z-[45] ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-slate-100 text-slate-700'} border-b`}
      style={{
        paddingTop: 'max(env(safe-area-inset-top, 0px), 0.625rem)'
      }}
    >

      <div className="flex items-center justify-between w-full px-2 sm:px-4 py-1 mx-auto">
        <div className="flex items-center">
          {/* Mobile menu button */}
          {currentUser?.role !== 'cari' && (
            <button
              onClick={onMenuToggle}
              className="inline-block p-2 mr-2 text-center text-slate-500 cursor-pointer rounded-lg lg:hidden"
            >
              <Bars3Icon className="w-6 h-6" />
            </button>
          )}

          <div
            className="flex flex-col items-start cursor-pointer justify-center"
            onClick={() => {
              if (currentUser?.role === 'cari') {
                navigate('/sectors/food-beverage/operations/distribution');
                return;
              }
              if (isAdminUser(currentUser)) {
                navigate('/dashboard/admin/');
                return;
              }
              if (!selectedBusiness || selectedBusiness.id === 'business_account' || selectedBusiness.business_type === 'business_account') {
                navigate('/dashboard/personal');
                return;
              }
              const sector = selectedBusiness.sector || selectedBusiness.business_type || '';
              const normalized = sector.toLowerCase();
              if (normalized === 'food_beverage' || normalized === 'food-beverage' || normalized === 'restaurant') {
                navigate('/sectors/food-beverage/dashboard');
              } else if (normalized === 'retail' || normalized === 'perakende') {
                navigate('/sectors/retail/dashboard');
              } else if (normalized === 'service' || normalized === 'hizmet') {
                navigate('/sectors/service/dashboard');
              } else {
                navigate('/sectors/food-beverage/dashboard');
              }
            }}
          >
            {/* Platform Logo */}
            <div className="flex flex-col items-start leading-none">
                <img
                    src={darkMode ? logoDark : logoLight}
                    className="h-7 sm:h-8 max-w-full transition-all duration-200"
                    alt="Sectorpilot"
                />
                {(currentUser?.role === 'cari' || currentUser?.user_type === 'cari') && (
                  <span className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-1 select-none">
                    {currentUser?.username || currentUser?.name || currentUser?.email}
                  </span>
                )}
            </div>
          </div>
        </div>

        <div className="flex items-center grow lg:flex lg:basis-auto">
          <div className="flex items-center ml-auto">
            {/* Search bar */}
            {currentUser?.role !== 'cari' && (
              <div className="hidden md:flex relative flex-wrap items-stretch w-full transition-all rounded-lg md:pr-4">
                <span className="text-sm ease-soft leading-5.6 absolute z-50 -ml-px flex h-full items-center whitespace-nowrap rounded-lg rounded-tr-none rounded-br-none border border-r-0 border-transparent bg-transparent py-2 px-2.5 text-center font-normal text-slate-500 transition-all">
                  <svg width="14px" height="14px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15.7955 15.8111L21 21M18 10.5C18 14.6421 14.6421 18 10.5 18C6.35786 18 3 14.6421 3 10.5C3 6.35786 6.35786 3 10.5 3C14.6421 3 18 6.35786 18 10.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-9 text-sm focus:shadow-soft-primary-outline ease-soft w-1/100 leading-5.6 relative -ml-px block min-w-0 flex-auto rounded-lg border border-solid border-gray-300 ${darkMode ? 'bg-gray-700 text-white placeholder:text-gray-400 border-gray-600' : 'bg-white text-gray-700'} bg-clip-padding py-2 pr-3 transition-all focus:border-fuchsia-300 focus:outline-none focus:transition-shadow`}
                  placeholder="Ürün, müşteri veya sistem sayfası ara..."
                />

                {showDropdown && (
                  <div 
                    className={`absolute top-full left-0 right-0 mt-1 p-4 rounded-2xl border border-gray-100 shadow-2xl z-[150] transition-all max-h-96 overflow-y-auto ${darkMode ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white text-gray-800'}`}
                    style={{ backgroundColor: darkMode ? '#1f2937' : '#ffffff', opacity: 1, transform: 'translate3d(0, 0, 0)' }}
                  >
                    {isSearching ? (
                      <div className="flex items-center justify-center py-6 text-xs font-black text-indigo-600 animate-pulse tracking-widest uppercase">
                        Sistem Taranıyor...
                      </div>
                    ) : (
                      <>
                        {searchResults.menu.length === 0 && searchResults.orders.length === 0 && searchResults.pages.length === 0 && (
                          <div className="py-4 text-center text-xs text-gray-500 font-black uppercase tracking-widest opacity-40 italic">Sonuç bulunamadı</div>
                        )}

                        {/* Sayfalar / Modüller / Müşteriler */}
                        {searchResults.pages.length > 0 && (
                          <div className="mb-4">
                            <span className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] block mb-2 px-2">Erişim & Navigasyon</span>
                            <div className="space-y-1">
                              {searchResults.pages.map((page, idx) => (
                                <div key={idx} onClick={() => { navigate(page.path); setShowDropdown(false); setSearchTerm(''); }} className={`p-3 rounded-xl cursor-pointer transition-all flex items-center justify-between text-[11px] font-black uppercase tracking-tight hover:${darkMode ? 'bg-gray-700' : 'bg-blue-50 text-blue-600'} group`}>
                                  <div className="flex items-center gap-3">
                                    <span className="opacity-40 group-hover:opacity-100">{page.type === 'customer' ? '👤' : '🧭'}</span> 
                                    <div>
                                      <p>{page.name}</p>
                                      {page.subtitle && <p className="text-[9px] font-bold lowercase italic text-slate-400 mt-1">{page.subtitle}</p>}
                                    </div>
                                  </div>
                                  <span className="opacity-0 group-hover:opacity-100 transition-all translate-x-2 group-hover:translate-x-0">→</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Menü Öğeleri */}
                        {searchResults.menu.length > 0 && (
                          <div className="mb-4">
                            <span className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] block mb-2 px-2">Menü Envanteri</span>
                            <div className="space-y-1">
                              {searchResults.menu.map(item => (
                                <div key={item.id} onClick={() => { navigate('/sectors/food-beverage/settings/menu'); setShowDropdown(false); setSearchTerm(''); }} className={`p-3 rounded-xl cursor-pointer transition-all flex items-center justify-between text-[11px] font-black uppercase tracking-tight hover:${darkMode ? 'bg-gray-700' : 'bg-emerald-50 text-emerald-600'} group`}>
                                  <div>
                                    <div className="font-black">{item.name}</div>
                                    <div className="text-[9px] text-slate-400 font-bold lowercase italic mt-1">{item.categoryName || 'Sektörel Ürün'}</div>
                                  </div>
                                  <div className="font-black text-indigo-600">₺{item.price}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Siparişler */}
                        {searchResults.orders.length > 0 && (
                          <div>
                            <span className="text-[10px] text-slate-400 font-black uppercase tracking-[0.2em] block mb-2 px-2">Aktif Operasyonlar</span>
                            <div className="space-y-1">
                              {searchResults.orders.map(order => (
                                <div key={order.id} onClick={() => { navigate('/sectors/food-beverage/operations/order-center'); setShowDropdown(false); setSearchTerm(''); }} className={`p-3 rounded-xl cursor-pointer transition-all text-[11px] font-black uppercase tracking-tight hover:${darkMode ? 'bg-gray-700' : 'bg-amber-50 text-amber-600'} group`}>
                                  <div className="flex justify-between items-center">
                                    <div className="font-black">#{order.orderNumber || order.id?.slice(0, 6)} - {order.customerName || order.customer?.name || 'Misafir'}</div>
                                    <span className="text-[10px] opacity-40">Masa {order.table_number || 'Paket'}</span>
                                  </div>
                                  <div className="text-[9px] text-slate-400 font-bold lowercase italic mt-1">Operasyonel Kayıt • ₺{order.totalAmount || order.total}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <ul className="flex flex-row justify-end pl-0 mb-0 list-none md-max:w-full">
            {/* Support Center */}
            {isAuthenticated && currentUser?.role !== 'cari' && (
              <li className="flex items-center px-1 sm:px-2">
                <button
                  onClick={() => navigate('/business-support-center')}
                  className={`flex items-center justify-center p-2 sm:px-4 sm:py-2 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${darkMode
                    ? 'bg-blue-900/30 text-blue-400 hover:bg-blue-900/50'
                    : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                    }`}
                >
                  <ChatBubbleBottomCenterTextIcon className="w-5 h-5 sm:w-4 sm:h-4" />
                  <span className="hidden lg:inline ml-2">Destek</span>
                </button>
              </li>
            )}

            {/* Dark mode */}
            <li className="hidden sm:flex items-center">
              <button
                onClick={toggleDarkMode}
                className={`p-2.5 text-sm font-semibold transition-all rounded-full ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-500 hover:bg-gray-100'}`}
              >
                {darkMode ? <SunIcon className="w-5 h-5" /> : <MoonIcon className="w-5 h-5" />}
              </button>
            </li>

            {/* Notifications */}
            {currentUser?.role !== 'cari' && (
              <li className="relative flex items-center px-1 sm:px-2">
                <Menu as="div" className="relative">
                  <Menu.Button className={`p-2 sm:p-2.5 text-sm transition-all rounded-full ${darkMode ? 'text-white hover:bg-gray-700' : 'text-slate-500 hover:bg-gray-100'}`}>
                    <div className="relative">
                      <BellIcon className="w-5 h-5" />
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 text-[0.6rem] font-black bg-rose-600 rounded-full flex justify-center items-center text-white animate-pulse">
                          {unreadCount}
                        </span>
                      )}
                    </div>
                  </Menu.Button>
                  <Menu.Items 
                    className={`absolute right-[-24px] sm:right-0 mt-3 w-[calc(100vw-24px)] sm:w-80 max-w-sm origin-top-right rounded-2xl shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
                    style={{ backgroundColor: darkMode ? '#1f2937' : '#ffffff', opacity: 1, transform: 'translate3d(0, 0, 0)' }}
                  >
                    <div className="py-4 px-6 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center">
                      <h3 className={`text-[11px] font-black uppercase tracking-widest ${darkMode ? 'text-white' : 'text-gray-900'}`}>Bildirimler</h3>
                      {unreadCount > 0 && <span className="text-[9px] font-black bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full uppercase tracking-tighter">{unreadCount} yeni</span>}
                    </div>
                    <div className="max-h-80 overflow-y-auto no-scrollbar">
                      {(!notifications || notifications.length === 0) ? (
                        <div className="p-10 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest opacity-30 italic">Yeni bildirim yok</div>
                      ) : (
                        notifications.map((notif) => {
                          const isRead = readIds.includes(notif.id);
                          return (
                            <Menu.Item key={notif.id}>
                              {({ active }) => (
                                <div
                                  onClick={() => {
                                    markAsRead(notif.id);
                                    if (notif.url) {
                                      navigate(notif.url);
                                    }
                                  }}
                                  className={`block w-full text-left px-6 py-4 text-xs border-b border-gray-50 dark:border-gray-700/50 last:border-0 cursor-pointer transition-all ${active ? (darkMode ? 'bg-gray-700' : 'bg-slate-50') : (isRead ? (darkMode ? 'bg-gray-800' : 'bg-white') : (darkMode ? 'bg-blue-900/10' : 'bg-blue-50/30'))}`}
                                >
                                  <div className="flex justify-between items-start mb-1.5">
                                    <span className={`text-[10px] font-black uppercase tracking-tight ${notif.category === 'whatsapp' ? 'text-emerald-600' : notif.type === 'warning' ? 'text-orange-500' : notif.type === 'error' ? 'text-rose-500' : 'text-blue-600'}`}>
                                      {notif.title}
                                      {!isRead && <span className={`ml-2 inline-block w-1.5 h-1.5 rounded-full ${notif.category === 'whatsapp' ? 'bg-emerald-600 shadow-[0_0_8px_rgba(5,150,105,0.6)]' : 'bg-blue-600 shadow-[0_0_8px_rgba(37,99,235,0.6)]'}`}></span>}
                                    </span>
                                    <span className="text-[8px] font-black text-slate-300 uppercase">
                                      {notif.createdAt?.toDate ? notif.createdAt.toDate().toLocaleDateString('tr-TR') : 'BUGÜN'}
                                    </span>
                                  </div>
                                  <p className={`text-[10px] font-bold leading-relaxed ${isRead ? 'text-slate-400' : 'text-slate-600 dark:text-gray-300'}`}>
                                    {notif.message}
                                  </p>
                                </div>
                              )}
                            </Menu.Item>
                          )
                        })
                      )}
                    </div>
                    <div className="p-3 bg-slate-50 dark:bg-gray-800/50 rounded-b-2xl">
                      <Link
                        to="/notifications"
                        className="block w-full py-3 text-center text-[10px] font-black text-blue-600 uppercase tracking-widest hover:bg-white dark:hover:bg-gray-700 rounded-xl transition-all shadow-sm"
                      >
                        BİLDİRİM MERKEZİNE GİT
                      </Link>
                    </div>
                  </Menu.Items>
                </Menu>
              </li>
            )}

            {/* BusinessSwitcher */}
            {isAuthenticated && (
              <li className="relative flex items-center px-1 sm:px-2">
                <BusinessSwitcher darkMode={darkMode} />
              </li>
            )}

            {/* User Profile */}
            <li className="relative flex items-center px-1">
              <Menu as="div" className="relative ml-1 sm:ml-2 z-[60]">
                <Menu.Button className="flex items-center gap-1.5 md:gap-3 p-1.5 md:pr-4 bg-white dark:bg-gray-800 rounded-full border border-slate-100 dark:border-gray-700 shadow-sm transition-all hover:shadow-md hover:border-blue-100 active:scale-95 group">
                  <div className={`w-9 h-9 flex items-center justify-center overflow-hidden border-2 rounded-full font-black text-sm transition-all group-hover:rotate-6 ${darkMode ? 'border-gray-700 bg-gray-900 text-white' : 'border-white bg-slate-900 text-white shadow-lg shadow-slate-200'}`}>
                    {userDisplayData?.photoURL || userDisplayData?.profileImageUrl ? (
                      <img src={userDisplayData.photoURL || userDisplayData.profileImageUrl} className="w-full h-full object-cover" alt="user" />
                    ) : (
                      <span>{userDisplayData?.name?.charAt(0)?.toUpperCase() || 'U'}</span>
                    )}
                  </div>
                  {isAuthenticated && (
                    <div className="hidden md:block text-left">
                      <div className={`text-[11px] font-black uppercase tracking-tight leading-none ${darkMode ? 'text-white' : 'text-slate-900'}`}>
                        {userDisplayData?.name || userDisplayData?.full_name || 'Kullanıcı'}
                      </div>
                      <div className={`text-[9px] font-bold text-slate-400 mt-1 lowercase italic leading-none`}>
                        {userDisplayData?.email || userDisplayData?.username}
                      </div>
                    </div>
                  )}
                </Menu.Button>
                <Menu.Items 
                  className={`absolute right-0 mt-3 w-56 origin-top-right rounded-2xl shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none z-50 overflow-hidden ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200'}`}
                  style={{ backgroundColor: darkMode ? '#1f2937' : '#ffffff', opacity: 1, transform: 'translate3d(0, 0, 0)' }}
                >
                  <div className="p-2">
                    <Menu.Item>
                      {({ active }) => (
                        <Link
                          to="/profile"
                          className={`flex items-center w-full text-left px-5 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${active ? (darkMode ? 'bg-gray-700' : 'bg-blue-50 text-blue-600') : (darkMode ? 'text-gray-200' : 'text-slate-600')}`}
                        >
                          <svg className="w-4 h-4 mr-3 opacity-60" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12,15.5A3.5,3.5 0 0,1 8.5,12A3.5,3.5 0 0,1 12,8.5A3.5,3.5 0 0,1 15.5,12A3.5,3.5 0 0,1 12,15.5M19.43,12.97C19.47,12.65 19.5,12.33 19.5,12C19.5,11.67 19.47,11.34 19.43,11L21.54,9.37C21.73,9.22 21.78,8.95 21.66,8.73L19.66,5.27C19.54,5.05 19.27,4.96 19.05,5.05L16.56,6.05C16.04,5.66 15.5,5.32 14.87,5.07L14.5,2.42C14.46,2.18 14.25,2 14,2H10C9.75,2 9.54,2.18 9.5,2.42L9.13,5.07C8.5,5.32 7.96,5.66 7.44,6.05L4.95,5.05C4.73,4.96 4.46,5.05 4.34,5.27L2.34,8.73C2.22,8.95 2.27,9.22 2.46,9.37L4.57,11C4.53,11.34 4.5,11.67 4.5,12C4.5,12.33 4.53,12.65 4.57,12.97L2.46,14.63C2.27,14.78 2.22,15.05 2.34,15.27L4.34,18.73C4.46,18.95 4.73,19.03 4.95,18.95L7.44,17.94C7.96,18.34 8.5,18.68 9.13,18.93L9.5,21.58C9.54,21.82 9.75,22 10,22H14C14.25,22 14.46,21.82 14.5,21.58L14.87,18.93C15.5,18.68 16.04,18.34 16.56,17.94L19.05,18.95C19.27,19.03 19.54,18.95 19.66,18.73L21.66,15.27C21.78,15.05 21.73,14.78 21.54,14.63L19.43,12.97Z"></path>
                          </svg>
                          Profil Ayarları
                        </Link>
                      )}
                    </Menu.Item>
                    <div className="h-px bg-slate-50 dark:bg-gray-700 my-1 mx-3" />
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={`flex items-center w-full text-left px-5 py-3.5 text-[10px] font-black uppercase tracking-widest rounded-xl transition-all ${active ? (darkMode ? 'bg-rose-900/20 text-rose-500' : 'bg-rose-50 text-rose-600') : (darkMode ? 'text-gray-400' : 'text-slate-500')}`}
                        >
                          <svg className="w-4 h-4 mr-3 opacity-60" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M16,17V14H9V10H16V7L21,12L16,17M14,2A2,2 0 0,1 16,4V6H14V4H5V20H14V18H16V20A2,2 0 0,1 14,22H5A2,2 0 0,1 3,20V4A2,2 0 0,1 5,2H14Z"></path>
                          </svg>
                          Güvenli Çıkış
                        </button>
                      )}
                    </Menu.Item>
                  </div>
                </Menu.Items>
              </Menu>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};

export default Header;
